import React from 'react';

import { KdsDepartmentOption } from '@/renderer/types/kds';
import { AppLogo } from '@components/common/AppLogo';

interface KdsDepartmentGateProps {
  departments: KdsDepartmentOption[];
  isLoading: boolean;
  onSelect: (departmentId: string) => void;
}

/**
 * Full-screen department picker shown before any board content — the user
 * MUST choose a department first (persisted afterwards), per the KDS
 * optimization requirement that every read is scoped to one department.
 */
export const KdsDepartmentGate: React.FC<KdsDepartmentGateProps> = ({
  departments,
  isLoading,
  onSelect,
}) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 text-center">
    <AppLogo size={64} />
    <div>
      <h2 className="text-2xl font-bold text-black">Select a department</h2>
      <p className="mt-1 text-base text-gray-600">
        Choose the department this screen will track. You can change it later
        from this same screen.
      </p>
    </div>

    {isLoading ? (
      <p className="text-sm text-gray-500">Loading departments…</p>
    ) : departments.length === 0 ? (
      <p className="max-w-sm text-sm text-gray-500">
        No departments were found for this outlet. Add a department from
        Dashboard → Departments first.
      </p>
    ) : (
      <div className="grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
        {departments.map((department) => (
          <button
            key={department.id}
            type="button"
            onClick={() => onSelect(department.id)}
            className="rounded-2xl border border-gray-200 bg-white px-4 py-6 text-base font-semibold text-gray-900 transition-colors hover:border-pos-primary hover:bg-pos-primary/5 focus:outline-none focus:ring-2 focus:ring-pos-primary"
          >
            {department.name}
          </button>
        ))}
      </div>
    )}
  </div>
);
